import type { TileableModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { DirectoryBrowseLoaderData } from '@modules/category/util/load-directory-browse.server';

type BrowsePayload = Pick<DirectoryBrowseLoaderData, 'browse' | 'pagination'>;

/**
 * A directory route's loader serves either the browse payload or the legacy
 * directory payload, and casts the browse branch out of its return type, so
 * meta functions tell them apart at runtime.
 */
const isDirectoryBrowsePayload = (data: object): data is BrowsePayload =>
    'isDirectoryBrowse' in data && data.isDirectoryBrowse === true;

export interface BrowseItemListTiles<T extends TileableModel> {
    tiles: T[];
    /** ItemList position of the first tile. */
    firstPosition: number;
    totalCount: number;
}

/**
 * The server-rendered browse grid tiles of the wanted kind, for ItemList
 * JSON-LD. `undefined` when the loader served the legacy directory payload.
 * The grid holds the first hydrated page, so positions count from there.
 */
export const getBrowseItemListTiles = <T extends TileableModel>(
    data: object,
    isWanted: (tile: TileableModel) => tile is T,
): BrowseItemListTiles<T> | undefined => {
    if (!isDirectoryBrowsePayload(data)) {
        return undefined;
    }
    const { browse, pagination } = data;
    return {
        tiles: browse.gridTiles.filter(isWanted),
        firstPosition: (browse.firstLoadedPage - 1) * pagination.pageSize + 1,
        totalCount: browse.gridTotalCount,
    };
};

/** Tile image sources are usually CDN URLs already; root-relative ones get the market host. */
export const toAbsoluteUrl = (baseUrl: string, src: string): string =>
    src.startsWith('/') ? baseUrl + src : src;
